/**
 * 第一方埋点。
 *
 * 设计取舍：
 *   · 自托管（上报到本项目 API），不引入任何第三方脚本——满足离线包「零外部依赖」约束；
 *   · fire-and-forget：任何失败都静默吞掉，绝不影响页面；
 *   · 会话标识为浏览器随机生成、存 sessionStorage，关闭标签即失效；
 *   · 不采集姓名/邮箱/IP；事件名与站点都受服务端白名单约束；
 *   · file:// 打开或静态托管环境下不做上报，避免噪音。
 */

export type TrackEvent = 'site_view' | 'nav_click' | 'lang_switch' | 'panel_focus';

interface QueuedEvent {
  event: TrackEvent;
  site: string;
  path?: string;
  locale?: string;
  sessionId: string;
  props?: string;
}

const SESSION_KEY = 'nanning_sid';
const FLUSH_DELAY_MS = 1200;

let queue: QueuedEvent[] = [];
let timer: ReturnType<typeof setTimeout> | undefined;

function sessionId(): string {
  try {
    let v = sessionStorage.getItem(SESSION_KEY);
    if (!v) {
      v = (globalThis.crypto?.randomUUID?.() ?? Math.random().toString(36).slice(2)).replace(/-/g, '').slice(0, 32);
      sessionStorage.setItem(SESSION_KEY, v);
    }
    return v;
  } catch {
    return 'anon';
  }
}

/** 是否应该上报：本地开发的 dev server / 线上服务端都上报；file:// 与静态离线包不上报 */
function enabled(): boolean {
  if (typeof window === 'undefined') return false;
  if (window.location.protocol === 'file:') return false;
  return true;
}

async function flush(): Promise<void> {
  if (queue.length === 0) return;
  const batch = queue;
  queue = [];
  try {
    await fetch('/api/analytics/events', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ events: batch }),
      keepalive: true,
    });
  } catch {
    // 静默：埋点失败不应影响页面（静态托管/后端未启动时都会走到这里）
  }
}

export function track(event: TrackEvent, site: string, opts?: { path?: string; locale?: string; props?: Record<string, unknown> }): void {
  if (!enabled()) return;
  queue.push({
    event,
    site,
    path: opts?.path,
    locale: opts?.locale,
    sessionId: sessionId(),
    props: opts?.props ? JSON.stringify(opts.props).slice(0, 400) : undefined,
  });
  if (timer) clearTimeout(timer);
  timer = setTimeout(() => void flush(), FLUSH_DELAY_MS);
}

/** 页面离开前把队列送出（keepalive 保证请求能完成） */
export function installFlushOnHide(): void {
  if (typeof window === 'undefined') return;
  window.addEventListener('pagehide', () => void flush());
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') void flush();
  });
}
